<template>
  <div class="profile-container">
    <!-- Page Header -->
    <div class="page-header">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="page-title">Mi Perfil</h3>
          <p class="text-muted mb-0">Administra tu información personal y configuración de cuenta</p>
        </div>
      </div>
    </div>

    <!-- Profile Overview Card -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card profile-card">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-auto">
                <div class="profile-avatar-wrapper">
                  <img :src="profileData.avatar || defaultAvatar" alt="Avatar" class="profile-avatar" />
                  <div class="avatar-badge">
                    <i class="fas fa-circle"></i>
                  </div>
                </div>
              </div>
              <div class="col">
                <h4 class="mb-1">{{ profileData.nombre }} {{ profileData.apellido }}</h4>
                <p class="text-muted mb-2">
                  <i class="fas fa-envelope me-2"></i>{{ profileData.email }}
                </p>
                <span class="badge bg-primary">{{ profileData.rol || 'Administrador' }}</span>
              </div>
              <div class="col-auto">
                <button class="btn btn-outline-primary" @click="editMode = !editMode">
                  <i class="fas fa-edit me-2"></i>
                  {{ editMode ? 'Cancelar' : 'Editar Perfil' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Mode / View Mode -->
    <div class="row">
      <!-- Profile Information -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-user me-2"></i>
              Información Personal
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveProfile">
              <div class="row">
                <!-- Nombre -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="nombre" class="form-label">Nombre</label>
                  <b-form-input
                    type="text"
                    v-model="profileData.nombre"
                    id="nombre"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="Ingresa tu nombre"
                  />
                </div>

                <!-- Apellido -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="apellido" class="form-label">Apellido</label>
                  <b-form-input
                    type="text"
                    v-model="profileData.apellido"
                    id="apellido"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="Ingresa tu apellido"
                  />
                </div>

                <!-- Email -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="email" class="form-label">Email</label>
                  <b-form-input
                    type="email"
                    v-model="profileData.email"
                    id="email"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                <!-- Teléfono -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="telefono" class="form-label">Teléfono</label>
                  <b-form-input
                    type="tel"
                    v-model="profileData.telefono"
                    id="telefono"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="+51 999 999 999"
                  />
                </div>

                <!-- Dirección -->
                <div class="col-12 mb-3 input-block">
                  <label for="direccion" class="form-label">Dirección</label>
                  <b-form-input
                    type="text"
                    v-model="profileData.direccion"
                    id="direccion"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="Ingresa tu dirección"
                  />
                </div>

                <!-- Ciudad y País -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="ciudad" class="form-label">Ciudad</label>
                  <b-form-input
                    type="text"
                    v-model="profileData.ciudad"
                    id="ciudad"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="Lima"
                  />
                </div>

                <div class="col-md-6 mb-3 input-block">
                  <label for="pais" class="form-label">País</label>
                  <b-form-input
                    type="text"
                    v-model="profileData.pais"
                    id="pais"
                    :disabled="!editMode"
                    class="form-control"
                    placeholder="Perú"
                  />
                </div>

                <!-- Bio -->
                <div class="col-12 mb-3 input-block">
                  <label for="bio" class="form-label">Biografía</label>
                  <textarea
                    class="form-control"
                    id="bio"
                    rows="3"
                    v-model="profileData.bio"
                    :disabled="!editMode"
                    placeholder="Cuéntanos algo sobre ti..."
                  ></textarea>
                </div>
              </div>

              <!-- Save Button -->
              <div class="text-end" v-if="editMode">
                <b-button type="submit" class="btn-primary m-1" :disabled="saving">
                  <span v-if="saving">
                    <i class="fas fa-spinner fa-spin me-2"></i>Guardando...
                  </span>
                  <span v-else>
                    <i class="fas fa-save me-2"></i>Guardar Cambios
                  </span>
                </b-button>
                <b-button @click="cancelEdit" class="btn-clean m-1">
                  Cancelar
                </b-button>
              </div>
            </form>
          </div>
        </div>

        <!-- Change Password Card -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-lock me-2"></i>
              Cambiar Contraseña
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="changePassword">
              <div class="row">
                <!-- Current Password -->
                <div class="col-12 mb-3 input-block">
                  <label for="currentPassword" class="form-label">Contraseña Actual</label>
                  <b-form-input
                    type="password"
                    v-model="passwordData.currentPassword"
                    id="currentPassword"
                    class="form-control"
                    placeholder="Ingresa tu contraseña actual"
                  />
                </div>

                <!-- New Password -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="newPassword" class="form-label">Nueva Contraseña</label>
                  <b-form-input
                    type="password"
                    v-model="passwordData.newPassword"
                    id="newPassword"
                    class="form-control"
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <!-- Confirm Password -->
                <div class="col-md-6 mb-3 input-block">
                  <label for="confirmPassword" class="form-label">Confirmar Contraseña</label>
                  <b-form-input
                    type="password"
                    v-model="passwordData.confirmPassword"
                    id="confirmPassword"
                    class="form-control"
                    placeholder="Repite la nueva contraseña"
                  />
                </div>

                <!-- Password Requirements -->
                <div class="col-12 mb-3">
                  <small class="text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    La contraseña debe tener al menos 6 caracteres
                  </small>
                </div>
              </div>

              <div class="text-end">
                <b-button type="submit" class="btn-primary m-1" :disabled="changingPassword">
                  <span v-if="changingPassword">
                    <i class="fas fa-spinner fa-spin me-2"></i>Cambiando...
                  </span>
                  <span v-else>
                    <i class="fas fa-key me-2"></i>Cambiar Contraseña
                  </span>
                </b-button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Sidebar with Avatar Upload & Stats -->
      <div class="col-lg-4">
        <!-- Avatar Upload Card -->
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-image me-2"></i>
              Foto de Perfil
            </h5>
          </div>
          <div class="card-body text-center">
            <div class="avatar-upload-wrapper mb-3">
              <img :src="avatarPreview || profileData.avatar || defaultAvatar" alt="Avatar" class="avatar-preview" />
            </div>
            <input
              class="form-control mb-2"
              type="file"
              @change="handleAvatarUpload"
              accept="image/*"
              ref="avatarInput"
            />
            <small class="text-muted d-block mb-3">
              <i class="fas fa-info-circle me-1"></i>
              JPG, PNG o GIF (máx. 2MB)
            </small>
            <b-button
              v-if="avatarPreview"
              @click="saveAvatar"
              class="btn-primary w-100"
              :disabled="uploadingAvatar"
            >
              <span v-if="uploadingAvatar">
                <i class="fas fa-spinner fa-spin me-2"></i>Subiendo...
              </span>
              <span v-else>
                <i class="fas fa-upload me-2"></i>Guardar Foto
              </span>
            </b-button>
          </div>
        </div>

        <!-- Account Info Card -->
        <div class="card mt-4">
          <div class="card-header">
            <h5 class="card-title mb-0">
              <i class="fas fa-info-circle me-2"></i>
              Información de Cuenta
            </h5>
          </div>
          <div class="card-body">
            <ul class="list-unstyled mb-0">
              <li class="mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted">
                    <i class="fas fa-calendar-alt me-2"></i>Miembro desde
                  </span>
                  <strong>{{ formatDate(profileData.createdAt) }}</strong>
                </div>
              </li>
              <li class="mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted">
                    <i class="fas fa-clock me-2"></i>Última actualización
                  </span>
                  <strong>{{ formatDate(profileData.updatedAt) }}</strong>
                </div>
              </li>
              <li class="mb-0">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted">
                    <i class="fas fa-shield-alt me-2"></i>Estado
                  </span>
                  <span class="badge bg-success">Activo</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import api from '@/api';
import authHelper from '@/utils/auth';

export default {
  name: 'Perfil',
  setup() {
    const editMode = ref(false);
    const saving = ref(false);
    const changingPassword = ref(false);
    const uploadingAvatar = ref(false);
    const avatarPreview = ref(null);
    const avatarInput = ref(null);

    const defaultAvatar = ref('https://ui-avatars.com/api/?name=Usuario&background=4e73df&color=fff&size=200');

    const profileData = ref({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      direccion: '',
      ciudad: '',
      pais: '',
      bio: '',
      avatar: '',
      rol: '',
      createdAt: '',
      updatedAt: ''
    });

    const profileDataOriginal = ref({});

    const passwordData = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });

    // Methods
    const fetchProfile = async () => {
      try {
        const response = await api.get('/auth/profile');
        profileData.value = { ...response.data };
        profileDataOriginal.value = { ...response.data };
      } catch (error) {
        console.error('Error al cargar perfil:', error);
        window.Swal.fire({
          title: 'Error',
          text: 'No se pudo cargar la información del perfil',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    };

    const saveProfile = async () => {
      try {
        saving.value = true;
        await api.put('/auth/profile', profileData.value);

        profileDataOriginal.value = { ...profileData.value };
        editMode.value = false;

        window.Swal.fire({
          title: 'Éxito',
          text: 'Perfil actualizado correctamente',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000
        });
      } catch (error) {
        console.error('Error al guardar perfil:', error);
        window.Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el perfil',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        saving.value = false;
      }
    };

    const cancelEdit = () => {
      profileData.value = { ...profileDataOriginal.value };
      editMode.value = false;
    };

    const changePassword = async () => {
      if (passwordData.value.newPassword !== passwordData.value.confirmPassword) {
        window.Swal.fire({
          title: 'Error',
          text: 'Las contraseñas no coinciden',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      if (passwordData.value.newPassword.length < 6) {
        window.Swal.fire({
          title: 'Error',
          text: 'La contraseña debe tener al menos 6 caracteres',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      try {
        changingPassword.value = true;
        await api.put('/auth/change-password', {
          currentPassword: passwordData.value.currentPassword,
          newPassword: passwordData.value.newPassword
        });

        passwordData.value = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };

        window.Swal.fire({
          title: 'Éxito',
          text: 'Contraseña actualizada correctamente',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000
        });
      } catch (error) {
        console.error('Error al cambiar contraseña:', error);
        window.Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo cambiar la contraseña',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        changingPassword.value = false;
      }
    };

    const handleAvatarUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2 * 1024 * 1024) {
          window.Swal.fire({
            title: 'Error',
            text: 'La imagen no debe superar los 2MB',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          avatarPreview.value = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };

    const saveAvatar = async () => {
      try {
        uploadingAvatar.value = true;
        const formData = new FormData();
        formData.append('avatar', avatarInput.value.files[0]);

        const response = await api.post('/auth/upload-avatar', formData);

        profileData.value.avatar = response.data.avatarUrl;
        profileDataOriginal.value.avatar = response.data.avatarUrl;
        avatarPreview.value = null;

        window.Swal.fire({
          title: 'Éxito',
          text: 'Foto de perfil actualizada correctamente',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000
        });
      } catch (error) {
        console.error('Error al subir avatar:', error);
        window.Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar la foto de perfil',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        uploadingAvatar.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    // Lifecycle
    onMounted(() => {
      fetchProfile();
    });

    return {
      editMode,
      saving,
      changingPassword,
      uploadingAvatar,
      avatarPreview,
      avatarInput,
      defaultAvatar,
      profileData,
      passwordData,
      saveProfile,
      cancelEdit,
      changePassword,
      handleAvatarUpload,
      saveAvatar,
      formatDate
    };
  }
};
</script>

<style scoped>
.profile-container {
  padding: 0;
}

.page-header {
  margin-bottom: 1.5rem;
}

.profile-card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.profile-card .text-muted {
  color: rgba(255, 255, 255, 0.8) !important;
}

.profile-avatar-wrapper {
  position: relative;
  display: inline-block;
}

.profile-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.3);
  object-fit: cover;
}

.avatar-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background-color: #28a745;
  border-radius: 50%;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-badge i {
  font-size: 8px;
  color: white;
}

.card {
  border: none;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 0;
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.avatar-upload-wrapper {
  position: relative;
  display: inline-block;
}

.avatar-preview {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: 4px solid #e9ecef;
  object-fit: cover;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.input-block {
  margin-bottom: 0;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  font-weight: 600;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-outline-primary {
  border-color: white;
  color: white;
  font-weight: 600;
}

.btn-outline-primary:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: white;
  color: white;
}

.btn-clean {
  background-color: #6c757d;
  color: white;
  border: none;
  font-weight: 600;
}

.btn-clean:hover {
  background-color: #5a6268;
  transform: translateY(-2px);
}

.list-unstyled li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.list-unstyled li:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .profile-avatar {
    width: 80px;
    height: 80px;
  }

  .avatar-preview {
    width: 120px;
    height: 120px;
  }
}
</style>
